import React from 'react';
import { Box, Card, CardContent, Chip, TableCell, TableSortLabel, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import moment from 'moment';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Peças visuais compartilhadas das telas de uso. Sem estado, sem request. Textos via `intl`.
// ---------------------------------------------------------------------------

export const PRIMARY = '#8BC34A';
export const SECONDARY = '#277afe';
export const KIND_COLORS = {
  read: '#3F51B4', write: '#8BC34A', export: '#FF9800', login: '#009688', logout: '#607D8B', screen: '#9C27B0',
  denied: '#F44336', permission_denied: '#6D4C41', role_denied: '#E91E63', blocked: '#B71C1C', rate_limited: '#FF5722', would_deny: '#FFC107', rate_limit_fail_open: '#795548',
};
export const GROUP_COLORS = { access: '#3F51B4', auth: '#009688', denial: '#F44336' };
export const PERMISSION_COLOR = '#6D4C41'; // recusa por claim (não é IDOR)
// Chaves = o ApiName que cada API grava (usage-audit.json): a Calleds grava "calledservice".
export const API_COLORS = { '2do': '#3F51B4', reports: '#8BC34A', calledservice: '#FF9800', front: '#9C27B0', '2talk': '#009688', unknown: '#9E9E9E' };

export const fmtInt = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('pt-BR'));
export const fmtMs = (ms) => {
  if (ms === null || ms === undefined) return '-';
  const n = Number(ms);
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)} s`;
  return `${Math.floor(n / 60000)}m ${Math.round((n % 60000) / 1000)}s`;
};
export const fmtDateTime = (d, withMs) => (d ? moment(d).format(withMs ? 'DD/MM/YYYY HH:mm:ss.SSS' : 'DD/MM/YYYY HH:mm:ss') : '-');
export const fmtTime = (d, withMs) => (d ? moment(d).format(withMs ? 'HH:mm:ss.SSS' : 'HH:mm:ss') : '-');
export const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export const durationColor = (ms) => {
  const n = Number(ms) || 0;
  if (n >= 2000) return '#F44336';
  if (n >= 500) return '#FF9800';
  return '#8BC34A';
};

export const statusColor = (code) => {
  const c = Number(code) || 0;
  if (c >= 500) return '#F44336';
  if (c >= 400) return '#FF9800';
  if (c >= 200) return '#8BC34A';
  return '#9E9E9E';
};

/** Rótulo traduzido de um action_kind. */
export const kindLabel = (intl, kind) => t(intl, `usage.kind.${kind}`, kind);

/** Delta percentual entre o período e o anterior. */
export const delta = (current, previous) => {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (p === 0) return c === 0 ? 0 : null; // null = "novo" (não havia base)
  return ((c - p) / p) * 100;
};

// ─────────────────────────── KPI ───────────────────────────

// onClick torna o card um filtro: `active` pinta o fundo e a borda na cor da família e mostra
// "filtrando" no canto; o mesmo clique no card ativo é o caller quem trata (liga/desliga).
export function UsageKpiCard({ intl, title, value, previous, format, icon, color, invert, subtitle, onClick, active }) {
  const fmt = format || fmtInt;
  const d = previous === undefined ? undefined : delta(value, previous);
  let deltaNode = null;
  if (d !== undefined) {
    if (d === null) deltaNode = <Typography variant="caption" style={{ color: '#888' }}>{t(intl, 'usage.kpi.new', 'novo')}</Typography>;
    else {
      const up = d > 0.05; const down = d < -0.05;
      const good = invert ? down : up; const bad = invert ? up : down;
      const c = good ? '#2e7d32' : bad ? '#c62828' : '#888';
      const Icon = up ? ArrowUpwardIcon : down ? ArrowDownwardIcon : RemoveIcon;
      deltaNode = (
        <Tooltip title={t(intl, 'usage.kpi.deltaTooltip', 'Variação em relação ao período anterior, de mesma duração')}>
          <span style={{ color: c, display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600 }}>
            <Icon style={{ fontSize: 14 }} /> {Math.abs(d).toFixed(1)}%
          </span>
        </Tooltip>
      );
    }
  }
  return (
    <Card onClick={onClick} style={{ border: `1px solid ${active ? (color || PRIMARY) : '#e0e0e0'}`, borderRadius: 12, borderLeft: `4px solid ${color || PRIMARY}`, height: '100%',
      cursor: onClick ? 'pointer' : undefined, backgroundColor: active ? `${color || PRIMARY}14` : undefined, transition: 'background-color .15s, border-color .15s' }}>
      <CardContent style={{ padding: '14px 16px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{title}</Typography>
          {active ? <Chip size="small" label={t(intl, 'usage.errors.filtering', 'filtrando')} style={{ height: 18, fontSize: 10, backgroundColor: color || PRIMARY, color: '#fff' }} />
            : icon ? <span style={{ color: color || PRIMARY, opacity: 0.8 }}>{icon}</span> : null}
        </Box>
        <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4, lineHeight: 1.2 }}>{fmt(value)}</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: 4, minHeight: 18 }}>
          <Typography variant="caption" style={{ color: '#888' }}>{subtitle || ''}</Typography>
          {deltaNode}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── chips ───────────────────────────

// Célula de texto longo (rota, url, tela, exceção): 1 linha, reticências e o valor inteiro no tooltip.
// maxWidth limita a coluna; sem isso a tabela cresce até a largura do maior valor e a página corta.
export function UsageEllipsisCell({ text, title, maxWidth = 260, mono, color, bold }) {
  const value = text === null || text === undefined || text === '' ? '-' : String(text);
  return (
    <Tooltip title={title !== undefined && title !== null && title !== '' ? String(title) : value} enterDelay={400}>
      <span style={{ display: 'block', maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined, color, fontWeight: bold ? 700 : undefined }}>{value}</span>
    </Tooltip>
  );
}

// Nome curto do CLIENTE a partir do User-Agent: primeiro o que não é navegador (script, ferramenta), depois
// o navegador. Serve para dizer na própria lista 'Direto na API · Python' sem abrir o drawer.
export const clientLabel = (intl, userAgent) => {
  const ua = String(userAgent || '').trim();
  if (!ua) return t(intl, 'usage.client.none', 'sem User-Agent');
  const tools = [[/python-requests/i, 'Python requests'], [/aiohttp/i, 'Python aiohttp'], [/python/i, 'Python'], [/curl\//i, 'curl'], [/wget/i, 'wget'], [/PostmanRuntime/i, 'Postman'], [/insomnia/i, 'Insomnia'],
    [/Go-http-client/i, 'Go'], [/okhttp/i, 'okhttp'], [/java\//i, 'Java'], [/axios|undici|node-fetch|node\.js/i, 'Node.js'], [/PowerShell/i, 'PowerShell'], [/Dart\//i, 'Dart'], [/libwww|HttpClient|RestSharp/i, 'HTTP client']];
  const hit = tools.find(([re]) => re.test(ua));
  if (hit) return hit[1];
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  if (/MSIE|Trident/.test(ua)) return 'Internet Explorer';
  return ua.split(/[/ ]/)[0].slice(0, 24);
};

// Navegador de verdade: todo navegador se apresenta como 'Mozilla/...'. PowerShell também começa assim e é
// excluído. Mesma regra do servidor (filtro de origem da aba Recusas).
export const isBrowserUa = (ua) => /^Mozilla\//i.test(String(ua || '')) && !/powershell/i.test(String(ua || ''));

// Evento sem visita de tela (sem os cabeçalhos X-Mexx-Screen*): a API foi chamada por fora do
// sistema (URL colada no navegador, ferramenta, script). É o padrão de teste/ataque de IDOR.
// client (opcional): nome curto do User-Agent, vira 'Direto na API · Python'.
export function UsageDirectApiChip({ intl, client }) {
  const label = t(intl, 'usage.directApi', 'Direto na API') + (client ? ` · ${client}` : '');
  return (
    <Tooltip title={t(intl, 'usage.directApi.tooltip', 'Requisição sem tela do sistema: feita por URL direta, ferramenta ou script (não pelo front).')}>
      <Chip size="small" variant="outlined" label={label} style={{ color: '#c62828', borderColor: '#ef9a9a', backgroundColor: '#ffebee', height: 20, fontSize: 11, fontWeight: 600 }} />
    </Tooltip>
  );
}

// ORIGEM de uma linha do histórico (toda linha é uma requisição de API): veio de uma tela do sistema
// (tem visita) → chip cinza 'Tela' + rota React; login/logout sem visita → chip cinza 'Login · Chrome'
// (a tela de entrada não manda visita); sem visita e navegador → 'Sem tela · Chrome' em âmbar (o próprio front
// fora de uma tela registrada, ou URL colada); sem visita e cliente que não é navegador → 'Direto na API ·
// Python' em vermelho. Só o último é 'fora do sistema'.
export function UsageOriginChip({ intl, row, maxWidth = 180 }) {
  if (!row) return null;
  const isScreen = row.actionKind === 'screen';
  if (isScreen || row.screenVisitId) {
    const screen = isScreen ? row.path : (row.screenRoute || row.screen || '');
    return (
      <Box display="inline-flex" alignItems="center" style={{ gap: 6, maxWidth: '100%' }}>
        <Chip size="small" variant="outlined" label={t(intl, 'usage.origin.screen', 'Tela')} style={{ color: '#455A64', borderColor: '#B0BEC5', backgroundColor: '#ECEFF1', height: 20, fontSize: 11 }} />
        <UsageEllipsisCell text={screen} title={row.screen || screen} maxWidth={maxWidth} color="#666" />
      </Box>
    );
  }
  if (row.actionKind === 'login' || row.actionKind === 'logout') {
    return <Chip size="small" variant="outlined" label={`${t(intl, 'usage.origin.auth', 'Login')} · ${clientLabel(intl, row.userAgent)}`} style={{ color: '#455A64', borderColor: '#B0BEC5', backgroundColor: '#ECEFF1', height: 20, fontSize: 11 }} />;
  }
  if (isBrowserUa(row.userAgent)) {
    return (
      <Tooltip title={t(intl, 'usage.origin.noScreen.tip', 'Navegador, mas sem tela do sistema: chamada do próprio front fora de uma tela registrada, ou URL colada no navegador.')}>
        <Chip size="small" variant="outlined" label={`${t(intl, 'usage.origin.noScreen', 'Sem tela')} · ${clientLabel(intl, row.userAgent)}`} style={{ color: '#E65100', borderColor: '#FFB74D', backgroundColor: '#FFF3E0', height: 20, fontSize: 11 }} />
      </Tooltip>
    );
  }
  return <UsageDirectApiChip intl={intl} client={clientLabel(intl, row.userAgent)} />;
}

// Classes de styles/mexx-global-override.css: coluna de ação fixa à direita na rolagem horizontal.
export const STICKY_CELL_CLASS = 'usage-cell-sticky';
// Linha marcada nos grids que detalham embaixo (IP, endpoint, rota com erro, tela). O estilo (barra azul +
// fundo em todas as células) está em styles/mexx-global-override.css; aqui só o nome, para as telas não divergirem.
export const SELECTED_ROW_CLASS = 'usage-row-selected';

// Recusa por CLAIM (o papel não tem a permissão da rota): tipo próprio 'permission_denied' desde 17/09/2026.
// A leitura antiga ('denied' + details.reason = permission) fica como reserva para linha gravada entre a
// migration e o deploy da 2DO. 'denied' puro é o IDOR de escopo (o usuário pediu registro que não é dele).
export const isPermissionDenial = (kind, details) => kind === 'permission_denied' || (kind === 'denied' && !!details && details.reason === 'permission');

export function UsageKindChip({ intl, kind, label, details }) {
  if (isPermissionDenial(kind, details)) {
    const chip = <Chip size="small" label={label || t(intl, 'usage.kind.permission', 'IDOR (permissão)')} style={{ backgroundColor: PERMISSION_COLOR, color: '#fff', fontWeight: 600, height: 22 }} />;
    return details && details.claim ? <Tooltip title={`${t(intl, 'usage.kind.permission.claim', 'Claim exigida')}: ${details.claim}`}>{chip}</Tooltip> : chip;
  }
  const color = KIND_COLORS[kind] || '#9E9E9E';
  return <Chip size="small" label={label || kindLabel(intl, kind)} style={{ backgroundColor: color, color: '#fff', fontWeight: 600, height: 22 }} />;
}

// Filtro por chips, padrão de TODAS as abas: rótulo à esquerda, 'Todos' + opções, uma escolhida por vez.
// options: [{ id, label, color?, count? }]. value '' = Todos. É filtro do SERVIDOR: quem usa recarrega no onChange.
export function UsageChipFilter({ label, allLabel, options, value, onChange, hint }) {
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4, marginBottom: 6 }}>
      {label && <Typography variant="caption" style={{ color: '#888', marginRight: 4 }}>{label}:</Typography>}
      <Chip size="small" label={allLabel} clickable color={!value ? 'primary' : 'default'} onClick={() => onChange('')} />
      {options.map((o) => {
        const on = String(value) === String(o.id);
        const text = o.count === undefined ? o.label : `${o.label} · ${fmtInt(o.count)}`;
        return <Chip key={o.id} size="small" label={text} clickable color={on ? 'primary' : 'default'} onClick={() => onChange(on ? '' : o.id)} style={on && o.color ? { backgroundColor: o.color, color: '#fff' } : undefined} />;
      })}
      {hint && <Typography variant="caption" style={{ color: '#888', marginLeft: 6 }}>{hint}</Typography>}
    </Box>
  );
}

// Opções padrão (mesmos nomes e cores em todas as abas).
export const originOptions = (intl) => [
  { id: 'screen', label: t(intl, 'usage.origin.screenFilter', 'Pela tela do sistema') },
  { id: 'browser', label: t(intl, 'usage.origin.browserFilter', 'Sem tela (navegador)'), color: '#E65100' },
  { id: 'external', label: t(intl, 'usage.origin.externalFilter', 'Fora do sistema'), color: '#c62828' },
];
export const resultOptions = (intl) => [
  { id: 'ok', label: t(intl, 'usage.ua.status.ok', 'Sucesso (< 400)'), color: '#2e7d32' },
  { id: 'error', label: t(intl, 'usage.ua.status.error', 'Erro (≥ 400)'), color: '#c62828' },
  { id: '4xx', label: t(intl, 'usage.ua.status.4xx', '4xx (recusa/validação)'), color: '#F57C00' },
  { id: '5xx', label: t(intl, 'usage.ua.status.5xx', '5xx (falha do servidor)'), color: '#B71C1C' },
];
// Tipo de evento: a lista de ações é de quem chama (cada aba mostra só as que fazem sentido nela).
export const kindOptions = (intl, kinds) => kinds.map((k) => ({ id: k, label: kindLabel(intl, k), color: KIND_COLORS[k] }));
export const identityOptions = (intl, withSystem) => [
  { id: 'user', label: t(intl, 'usage.ua.userKind.user', 'Usuário (sessão)') },
  { id: 'integration', label: t(intl, 'usage.ua.userKind.integration', 'Chave de integração'), color: '#6A1B9A' },
  { id: 'anonymous', label: t(intl, 'usage.ua.userKind.anonymous', 'Anônimo'), color: '#546E7A' },
  ...(withSystem ? [{ id: 'system', label: t(intl, 'usage.ua.userKind.system', 'Sistema') }] : []),
];

// De onde vem o número e de quando é. source: 'rollup' (resumo diário, refeito de hora em hora) | 'ip' (resumo por IP) |
// 'fact' (histórico, tempo real) | 'memory' (redes conhecidas). ctx = { rollupAt } vindo do index.js.
export function UsageSourceTag({ intl, source, ctx }) {
  const at = ctx && ctx.rollupAt ? moment(ctx.rollupAt).format('DD/MM HH:mm') : null;
  const text = source === 'audit' ? t(intl, 'usage.source.audit', 'trilha de escrita (trigger no banco) · tempo real')
    : source === 'outbox' ? t(intl, 'usage.source.outbox', 'outbox de webhooks · tempo real')
    : source === 'fact' ? t(intl, 'usage.source.fact', 'histórico · tempo real')
    : source === 'memory' ? t(intl, 'usage.source.memory', 'redes conhecidas · atualizado de hora em hora')
      : `${source === 'ip' ? t(intl, 'usage.source.ip', 'resumo por IP') : t(intl, 'usage.source.rollup', 'resumo consolidado')}${at ? ` · ${t(intl, 'usage.source.at', 'atualizado')} ${at}` : ` · ${t(intl, 'usage.source.hourly', 'refeito de hora em hora')}`}`;
  const tip = source === 'audit' ? t(intl, 'usage.source.audit.tip', 'Gravado pelo próprio banco a cada inclusão, alteração ou exclusão nas tabelas administrativas — pega qualquer caminho de escrita (API, worker ou SQL direto).')
    : source === 'outbox' ? t(intl, 'usage.source.outbox.tip', 'Eventos de chamado, chat, usuário, organização, categoria e equipe gravados pelas triggers do outbox de webhooks (tb_calleds_outbox e archive), traduzidos para o mesmo de-para.')
    : source === 'fact' ? t(intl, 'usage.source.fact.tip', 'Lido direto do histórico de uso: inclui o que acabou de acontecer.')
    : t(intl, 'usage.source.rollup.tip', 'Lido do resumo, que é recalculado de hora em hora: pode faltar a última hora em relação às listas em tempo real.');
  return <Tooltip title={tip}><span style={{ fontSize: 11, color: '#888', border: '1px solid #e0e0e0', borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>{text}</span></Tooltip>;
}

// Cabeçalho ordenável pelo SERVIDOR (lista fechada do Reports). sort: '' (mais recente primeiro) | when_asc | duration_desc | status_desc.
// sortKey: 'when' alterna recente ↔ antigo; 'duration' / 'status' ligam a ordenação decrescente e, no segundo clique, voltam ao padrão.
export function UsageSortCell({ label, sortKey, sort, onSort, align }) {
  const current = sort || '';
  const isWhen = sortKey === 'when';
  const target = isWhen ? 'when_asc' : `${sortKey}_desc`;
  const active = isWhen ? (current === '' || current === 'when_asc') : current === target;
  const direction = isWhen && current === 'when_asc' ? 'asc' : 'desc';
  const next = isWhen ? (current === 'when_asc' ? '' : 'when_asc') : (current === target ? '' : target);
  return <TableCell align={align} sortDirection={active ? direction : false}><TableSortLabel active={active} direction={direction} onClick={() => onSort(next)}>{label}</TableSortLabel></TableCell>;
}

export function UsageStatusChip({ code }) {
  if (code === null || code === undefined) return <span style={{ color: '#bbb' }}>—</span>;
  return <Chip size="small" label={code} style={{ backgroundColor: statusColor(code), color: '#fff', fontWeight: 600, height: 22, minWidth: 44 }} />;
}

export function UsageApiChip({ api }) {
  return <Chip size="small" variant="outlined" label={api} style={{ borderColor: API_COLORS[api] || '#9E9E9E', color: API_COLORS[api] || '#9E9E9E', height: 22, fontWeight: 600 }} />;
}

// ─────────────────────────── barra de duração ───────────────────────────

export function UsageDurationBar({ ms, max }) {
  if (ms === null || ms === undefined) return <span style={{ color: '#bbb' }}>—</span>;
  const pct = Math.max(2, Math.min(100, (Number(ms) / Math.max(1, max || 5000)) * 100));
  return (
    <Box display="flex" alignItems="center" style={{ minWidth: 120 }}>
      <Box style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3, marginRight: 8 }}>
        <Box style={{ width: `${pct}%`, height: 6, background: durationColor(ms), borderRadius: 3 }} />
      </Box>
      <Typography variant="caption" style={{ minWidth: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(ms)}</Typography>
    </Box>
  );
}

// ─────────────────────────── ranking (barras horizontais clicáveis) ───────────────────────────

export function UsageRankBar({ intl, items, labelKey, valueKey, format, color, onClick, selected, secondary }) {
  const max = Math.max(1, ...items.map((i) => Number(i[valueKey]) || 0));
  const fmt = format || fmtInt;
  if (!items.length) return <UsageEmpty intl={intl} compact />;
  return (
    <Box>
      {items.map((it, idx) => {
        const v = Number(it[valueKey]) || 0;
        const label = it[labelKey];
        const isSel = selected && selected === label;
        const c = typeof color === 'function' ? color(it) : (color || SECONDARY);
        return (
          <Box key={`${label}-${idx}`} onClick={onClick ? () => onClick(it) : undefined}
            style={{ padding: '6px 8px', borderRadius: 8, cursor: onClick ? 'pointer' : 'default', background: isSel ? '#f0f7ff' : 'transparent', marginBottom: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Tooltip title={label || ''}><Typography variant="body2" noWrap style={{ maxWidth: '70%', fontWeight: isSel ? 700 : 500 }}>{label || '—'}</Typography></Tooltip>
              <Typography variant="body2" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(v)}{secondary ? <span style={{ color: '#888', fontWeight: 400, marginLeft: 6 }}>{secondary(it)}</span> : null}</Typography>
            </Box>
            <Box style={{ height: 8, background: '#f0f0f0', borderRadius: 4, marginTop: 4 }}>
              <Box style={{ width: `${(v / max) * 100}%`, height: 8, background: c, borderRadius: 4, transition: 'width .3s' }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─────────────────────────── mapa de calor hora × dia da semana ───────────────────────────

const WEEKDAY_DEFAULTS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

export function UsageHeatmap({ intl, cells }) {
  const grid = {};
  let max = 1;
  (cells || []).forEach((c) => { grid[`${c.weekday}-${c.hour}`] = c.events; if (c.events > max) max = c.events; });
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const weekdays = WEEKDAY_DEFAULTS.map((d, i) => t(intl, `usage.wd.${i + 1}`, d));
  const eventsLabel = t(intl, 'usage.heat.events', 'eventos');
  return (
    <Box style={{ overflowX: 'auto' }}>
      <Box style={{ display: 'grid', gridTemplateColumns: `44px repeat(24, minmax(18px, 1fr))`, gap: 2, minWidth: 560 }}>
        <span />
        {hours.map((h) => <Typography key={`h${h}`} variant="caption" align="center" style={{ color: '#888', fontSize: 10 }}>{h}</Typography>)}
        {weekdays.map((wd, i) => (
          <React.Fragment key={wd}>
            <Typography variant="caption" style={{ color: '#666', fontWeight: 600, lineHeight: '18px' }}>{wd}</Typography>
            {hours.map((h) => {
              const v = grid[`${i + 1}-${h}`] || 0;
              const alpha = v === 0 ? 0.06 : 0.15 + 0.85 * (v / max);
              return (
                <Tooltip key={`${wd}-${h}`} title={`${wd} ${h}h: ${fmtInt(v)} ${eventsLabel}`}>
                  <Box style={{ height: 18, borderRadius: 3, background: `rgba(39,122,254,${alpha.toFixed(2)})` }} />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

// ─────────────────────────── vazio ───────────────────────────

export function UsageEmpty({ intl, title, description, icon, compact }) {
  if (compact) return <Typography variant="body2" style={{ color: '#999', padding: 12, textAlign: 'center' }}>{title || t(intl, 'usage.empty.compact', 'Sem dados no período')}</Typography>;
  return (
    <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}>
      <CardContent style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: '#bdbdbd', marginBottom: 8 }}>{icon}</div>
        <Typography style={{ fontSize: 18, fontWeight: 600 }}>{title || t(intl, 'usage.empty.title', 'Nada por aqui ainda')}</Typography>
        <Typography style={{ fontSize: 14, color: '#888', marginTop: 4 }}>{description || t(intl, 'usage.empty.description', 'O histórico de uso começa a contar a partir da ativação. Ajuste o período ou volte mais tarde.')}</Typography>
      </CardContent>
    </Card>
  );
}

/**
 * Item 6 — atribuição exigida pela licença da base de geolocalização (DB-IP Lite, Creative Commons Atribuição 4.0):
 * "you must include a link back to DB-IP.com on pages that display or use results from the database". Fica no rodapé das
 * telas que mostram país/operadora/tipo de rede e no drawer do evento. O texto do link é o pedido pela DB-IP; não traduzir.
 */
export function UsageGeoAttribution({ intl, style }) {
  return (
    <Typography variant="caption" style={{ color: '#999', display: 'block', marginTop: 8, ...(style || {}) }}>
      {t(intl, 'usage.geo.attribution', 'País e operadora dos IPs:')}{' '}
      <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>IP Geolocation by DB-IP</a>
    </Typography>
  );
}

/** Título de bloco do dashboard. */
export function UsageBlockTitle({ title, subtitle, right }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
      <Box>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" style={{ color: '#888' }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Box>
  );
}
